import Input from '@/components/forms/Input'
import Button from '@/components/misc/Button'
import { SubmitHandler, useForm } from 'react-hook-form'
import Select from '@/components/forms/Select'
import TextArea from '@/components/forms/TextArea'
import { companyTypeOptions } from '../filters/constants/statusOptions'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createNewCompany, editNewCompany } from '@/api/companyAPI'
import { GET_ALL_COMPANIES_QUERY_KEY } from '../hooks/useServerLogisticCompany'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { Company } from '@/interfaces/company'

interface IProp {
  close: () => void
  companyToEdit?: Company
}
export default function NewCompanyModal({ close, companyToEdit }: IProp) {
  const { control, handleSubmit, setError } = useForm()
  const queryClient = useQueryClient()
  const [isSending, setIsSending] = useState(false)

  const createCompany = useMutation({
    mutationFn: createNewCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_ALL_COMPANIES_QUERY_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: ['providers'],
      })
      queryClient.invalidateQueries({
        queryKey: ['managers'],
      })
      setIsSending(false)
      close()
      toast.success('La compañía ha sido creada con éxito.')
    },
  })

  const editCompany = useMutation({
    mutationFn: editNewCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_ALL_COMPANIES_QUERY_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: ['providers'],
      })
      queryClient.invalidateQueries({
        queryKey: ['managers'],
      })
      setIsSending(false)
      close()
      toast.success('La compañía ha sido editada con éxito.')
    },
    onError: (response: Error) => {
      setIsSending(false)
      close()
      toast.error(
        JSON.stringify(
          (response as { response?: { statusText?: string } }).response
            ?.statusText || 'Lo siento, ha ocurrido un error intentando editar'
        )
      )
    },
  })

  const onSubmit: SubmitHandler<
    Record<string, string | number | boolean | string[]>
  > = (data) => {
    if (!data.company_name) {
      setError(
        'company_name',
        {
          type: 'manual',
          message: '¡Este campo es requerido!',
        },
        { shouldFocus: true }
      )
      return
    }
    if (!data.company_type) {
      setError(
        'company_type',
        {
          type: 'manual',
          message: '¡Este campo es requerido!',
        },
        { shouldFocus: true }
      )
      return
    }
    setIsSending(true)
    if (companyToEdit) {
      editCompany.mutate({
        id: companyToEdit.id,
        name: data.company_name as string,
        type: data.company_type as string,
        observations: data.observations as string,
      })
    } else {
      createCompany.mutate({
        name: data.company_name as string,
        type: data.company_type as string,
        observations: data.observations as string,
      })
    }
  }

  return (
    <div className={'flex h-full flex-col space-y-4'}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h1 className={'mb-5 text-xl font-bold text-slate-700'}>
          {companyToEdit ? 'Editar compañía' : 'Nueva compañía'}
        </h1>
        <div className="pb-6">
          <Input
            control={control}
            defaultValue={companyToEdit?.name}
            label={'Nombre (*)'}
            name={'company_name'}
            placeholder={'Nombre...'}
          />
        </div>
        <div className="pb-6">
          <Select
            control={control}
            data={companyTypeOptions
              .map((status) => ({
                id: status.value,
                name: status.label,
              }))
              .filter((item) => item.name !== 'Todos')}
            defaultValue={companyToEdit?.type}
            label={'Tipo (*)'}
            name={'company_type'}
            placeholder={'Seleccione...'}
          />
        </div>
        <div className="pb-2">
          <TextArea
            control={control}
            defaultValue={companyToEdit?.observations}
            label={'Observaciones'}
            name={'observations'}
            placeholder={'Observaciones...'}
            textareaClassName={'h-24'}
          />
        </div>
        <div className={'mt-10 flex w-full justify-end'}>
          <Button
            disabled={isSending}
            loading={isSending}
            name={'Guardar'}
            type="submit"
          />
        </div>
      </form>
    </div>
  )
}
